<template>
  <div
    v-if="canLoadGameScreen"
    class="backdrop"
    :style="{ backgroundColor: backgroundColor }"
  >
    <GameView
      ref="gameViewInstance"
      :username="username"
      :gameRoomcode="gameRoomcode"
      :gameState="gameState"
      :primaryColor="primaryColor"
      :isHost="isHost"
      :totalSecondsRemaining="totalSecondsRemaining"
      :timeRemaining="timeRemaining"
      :key="gameViewKey"
      @play-sound-effect="playSoundEffect($event)"
    />
    <VideoView 
      ref="videoViewInstance"
      :localTracks="localTracks"
      :videoRoomcode="videoRoomcode"
      :gameState="gameState"
      :isHost="isHost"
      :isLocalAudioMuted="isLocalAudioMuted"
      :isLocalVideoMuted="isLocalVideoMuted"
      :primaryColor="primaryColor"
      :secondaryColor="secondaryColor"
      :backgroundColor="backgroundColor"
      @play-sound-effect="playSoundEffect($event)"
    />
    <GameFooter
      ref="gameFooterInstance"
      :gameRoomcode="gameRoomcode"
      :displayTimer="displayTimer"
      :isLocalAudioMuted="isLocalAudioMuted"
      :isLocalVideoMuted="isLocalVideoMuted"
      :isHost="isHost"
      :isMusicMutedInitially="isMusicMutedInitially"
      @disable-local-audio-tracks="disableLocalAudioTracks"
      @enable-local-audio-tracks="enableLocalAudioTracks"
      @disable-local-video-tracks="disableLocalVideoTracks"
      @enable-local-video-tracks="enableLocalVideoTracks"
    />
    <RoundStartView
      ref="roundStartViewInstance"
      v-show="displayRoundStartView"
      :roundIndex="'roundIndex' in gameState ? gameState.roundIndex : -1"
    />
    <transition name="fade-slow">
      <div
        v-show="displayPlayAgainButton && isHost"
        class="play-again-button"
        @click="createNewGame"
      >
        <div class="play-again-button-content">
          <span
            v-show="!playAgainButtonClicked"
          >
            <font-awesome-icon :icon="['fas', 'redo-alt']" />
          </span>
          <span
            v-show="!playAgainButtonClicked"
          >
            play again
          </span>
          <Spinner
            color="teal"
            v-show="playAgainButtonClicked"
          />
        </div>
      </div>
    </transition>
    <transition name="fade-slow">
      <div
        class="weavr-spinner-container"
        v-if="displayWeavrSpinner"
      >
        <img class="weavr-spinner" src="@/assets/weavr-logo.svg">
      </div>
    </transition>
    <transition name="fade-slow">
      <div
        v-show="displayFollowUpGameMessage"
        class="follow-up-game-message-container"
      >
        <span class="follow-up-game-message">
          {{ followUpGameMessage }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue';
import GameView from '@/components/gameComponents/GameView';
import VideoView from '@/components/gameComponents/VideoView';
import GameFooter from '@/components/gameComponents/GameFooter.vue';
import RoundStartView from '@/components/gameComponents/RoundStartView.vue';
import Spinner from '@/components/utilComponents/Spinner';

export default Vue.extend({
  name: 'GameScreen',
  components: {
    GameView,
    VideoView,
    GameFooter,
    RoundStartView,
    Spinner
  },
  props: {
    username: String,
    initialGameRoomcode: String,
    videoRoomcode: String,
    localTracks: Array,
    isMusicMutedInitially: Boolean,
    wasRedirectedFromGameSetupScreen: Boolean
  },
  data: () => ({
    canLoadGameScreen: false,
    gameRoomcode: '',
    socket: {},
    playerInfo: {},
    gameState: {},
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    displayTimer: false,
    displayRoundStartView: false,
    isLocalAudioMuted: false,
    isLocalVideoMuted: false,
    displayWeavrSpinner: false,
    displayPlayAgainButton: false,
    playAgainButtonClicked: false,
    displayFollowUpGameMessage: false,
    totalSecondsRemaining: 0,
    timeRemaining: '',
    hasStartedLobbySong: false,
    gameViewKey: 0,
    pingSetIntervalId: null,
    timerSetIntervalId: null
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    gameStatus: function () {
      return this.gameState.gameStatus || '';
    },
    isHost: function () {
      if ('hostUserId' in this.gameState) {
        return this.gameState.hostUserId === this.auth0UserId;
      }
      return false;
    },
    isTimestampAvailable: function () {
      return 'countdownTimestamp' in this.gameState && this.gameState.countdownTimestamp.length > 0;
    },
    followUpGameMessage: function () {
      if (this.isHost) {
        return "You've started a new game";
      }
      return 'The host has started a new game';
    }
  },
  methods: {
    setGameRoomcode(gameRoomcodeToSet) {
      this.gameRoomcode = gameRoomcodeToSet;
    },
    connectSocket() {
      const newSocket = new WebSocket(`${ process.env.VUE_APP_BACKEND_SOCKET_URL }`);
      newSocket.onopen = () => {
        this.socket = newSocket;
        this.socket.onmessage = (event) => {
          const socketData = JSON.parse(event.data);

          const socketHandlerTable = {
            'gameStateUpdated': () => {
              this.getGameState();
            },
            'playerHasAnswered': () => {
              if (socketData.identity === this.auth0UserId) {
                this.$refs.videoViewInstance.localPlayerHasAnswered = true;
              } else {
                this.$refs.videoViewInstance.setPlayerHasAnswered(socketData.identity);
              }
            },
            'playerHasVoted': () => {
              if (socketData.identity === this.auth0UserId) {
                this.$refs.videoViewInstance.localPlayerHasVoted = true;
              } else {
                this.$refs.videoViewInstance.setPlayerHasVoted(socketData.identity);
              }
            },
            'playerHasLeftGame': () => {
              this.gameState.gameStatus = socketData.gameStatus;
              this.gameState.hostUserId = socketData.hostUserId;
              this.$refs.gameViewInstance.$refs.waitViewInstance.checkGameHasFreePlayers();
            },
            'newGameRoomcodeIssued': () => {
              this.displayPlayAgainButton = false;
              this.displayWeavrSpinner = true;
              this.gameViewKey += 1;  // force re-render of GameView
              this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('lobby');
              this.disconnectSocket();  // disconnect socket from previous room
              this.setGameRoomcode(socketData.newGameRoomcode);
              this.connectSocket();  // connect socket to new room
              this.displayFollowUpGameMessage = true;
              this.getGameState();
            }
          };

          if (socketData.code in socketHandlerTable) {
            socketHandlerTable[socketData.code]();
            // console.log('gameState: ', this.gameState);
          }
        };

        this.socket.send(JSON.stringify({
          action: 'attachSocketToRoom',
          gameRoomcode: this.gameRoomcode
        }));

        this.pingSetIntervalId = setInterval(() => {
          this.socket.send(JSON.stringify({
            action: 'ping'
          }));
        }, Number(process.env.VUE_APP_SOCKET_HEARTBEAT_INTERVAL));
      };
    },
    disconnectSocket() {
      try {
        this.socket.send(JSON.stringify({
          action: 'broadcast',
          gameRoomcode: this.gameRoomcode,
          data: JSON.stringify({
            code: 'remotePlayerSocketDisconnect',
            identity: this.auth0UserId
          })
        }));

        this.socket.send(JSON.stringify({
          action: 'detachSocketFromRoom',
          gameRoomcode: this.gameRoomcode
        }));

        this.socket.close();
      } catch (error) {
        console.log('Error on socket disconnect:', error);
      }
    },
    removePlayerFromGame() {
      navigator.sendBeacon(`${ process.env.VUE_APP_BACKEND_API_URL }/leavegame`, JSON.stringify({
        auth0UserId: this.auth0UserId,
        gameRoomcode: this.gameRoomcode
      }));
    },
    exitGame() {
      if (process.env.VUE_APP_ENABLE_VIDEO === 'true') {
        this.$refs.videoViewInstance.disconnectFromTwilioRoom();
      }
      this.removePlayerFromGame();
      this.disconnectSocket();
    },
    preventPageUnload (event) {
      event.preventDefault();
      event.returnValue = '';
    },
    setPlayerColors () {
      const playerIndex = this.gameState.playerUserIds.findIndex(userId => userId === this.auth0UserId);
      this.primaryColor = this.gameState.playerPrimaryColors[playerIndex];
      this.secondaryColor = this.gameState.playerSecondaryColors[playerIndex];
      this.backgroundColor = this.gameState.playerBackgroundColors[playerIndex];
    },
    getGameState () {
      this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/gamestate`, {
        auth0UserId: this.auth0UserId,
        username: this.username,
        gameRoomcode: this.gameRoomcode
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      }).then(response => {
        this.gameState = response.data.gameState;
        this.setPlayerColors();
        this.processGameState();
        this.displayWeavrSpinner = false;

        setTimeout(() => {
          this.displayFollowUpGameMessage = false;
        }, 10000);
      }).catch(error => {
        console.log(error);
      });
    },
    async processGameState () {
      // Stop sound effects just in case
      if (this.gameState.roundIndex >= 0) {
        this.stopAllSoundEffects();
      }

      if (this.gameState.turnIndex < 0) {
        // Round hasn't started yet, so clear gameViewInstance and display prompts in playerViewInstance

        // Stop lobby music once game starts
        if (this.gameState.roundIndex === 0) {
          this.$refs.gameFooterInstance.$refs.musicPlayerInstance.stopSong();
        }

        this.$refs.videoViewInstance.resetPlayersHaveVoted();
        this.$refs.gameViewInstance.displayVoteView = false;
        this.$refs.gameViewInstance.displayVoteResultView = false;
        await (new Promise(resolve => {
          setTimeout(resolve, 400);
        }));

        if (this.gameState.roundIndex >= 0) {
          // Game is running
          this.$refs.gameViewInstance.displayWaitView = false;
          this.$refs.gameFooterInstance.displayGameRoomcode = true;

          if (this.gameState.roundIndex > 0) {
            // If not the first round, display the previous round's last turn results and round results
            this.displayTimer = false;
            this.$refs.gameViewInstance.previousTurnResult = this.gameState.previousTurnResult;
            this.$refs.gameViewInstance.displayVoteResultView = true;
            await (new Promise(resolve => {
              setTimeout(resolve, 10000);
            }));
            this.$refs.gameViewInstance.displayVoteResultView = false;

            await (new Promise(resolve => {
              setTimeout(resolve, 400);
            }));

            if (this.gameState.roundIndex === this.gameState.numRounds) {
              // game has ended, start playing ending song when showing final scoreboard
              this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('ending');
            }

            this.$refs.gameViewInstance.displayRoundResultView = true;
            this.$refs.gameViewInstance.$refs.roundResultViewInstance.scoreboard = this.gameState.scoreboard;
            await (new Promise(resolve => {
              setTimeout(resolve, 8000);
            }));

            if (this.gameState.roundIndex < this.gameState.numRounds) {
              this.$refs.gameViewInstance.$refs.roundResultViewInstance.scoreboard = [];
              await (new Promise(resolve => {
                setTimeout(resolve, 300);
              }));
              this.$refs.gameViewInstance.displayRoundResultView = false;
            } else {
              // game has ended
              this.$refs.gameViewInstance.displayGameEndView = true;
              await (new Promise(resolve => {
                setTimeout(resolve, 5000);
              }));
              this.$refs.gameViewInstance.displayGameEndView = false;
              this.displayPlayAgainButton = true;
            }
          }

          if (this.gameState.roundIndex < this.gameState.numRounds) {
            this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('prompts');
            this.displayRoundStartView = true;

            await (new Promise(resolve => {
              setTimeout(resolve, 4000);
            }));

            this.displayRoundStartView = false;
            this.displayTimer = true;
            this.updateTimeRemaining();  // ensure this.totalSecondsRemaining is not empty for GameView.PromptTimerAnimation
            this.$refs.gameViewInstance.displayQuestionView = true;
            this.$refs.gameFooterInstance.gameState = this.gameState;
          }
        } else {
          this.$refs.gameViewInstance.displayWaitView = true;
          this.$refs.gameFooterInstance.displayGameRoomcode = false;

          if (!this.hasStartedLobbySong) {
            this.hasStartedLobbySong = true;
            this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('lobby');
          }
        }
      } else {
        // Round has started, clear playerViewInstance, display turns in gameViewInstance
        this.$refs.gameViewInstance.displayWaitView = false;
        this.$refs.gameFooterInstance.displayGameRoomcode = true;
        this.$refs.videoViewInstance.resetPlayersHaveAnswered();
        this.$refs.gameViewInstance.displayQuestionView = false;

        if (this.gameState.turnIndex > 0) {
          // If not the first turn, display the previous turn's result
          this.displayTimer = false;
          this.$refs.gameViewInstance.displayVoteStartView = false;
          this.$refs.videoViewInstance.resetPlayersHaveVoted();
          this.$refs.gameViewInstance.displayVoteView = false;
          this.$refs.gameViewInstance.displayVoteResultView = false;
          await (new Promise(resolve => {
            setTimeout(resolve, 400);
          }));
          this.$refs.gameViewInstance.previousTurnResult = this.gameState.previousTurnResult;
          this.$refs.gameViewInstance.displayVoteResultView = true;

          if ('turnInfo' in this.gameState && this.gameState.turnInfo.wereAllAnswersSubmitted) {
            await (new Promise(resolve => {
              setTimeout(resolve, 10000);
            }));

            this.$refs.gameViewInstance.displayVoteResultView = false;
            await (new Promise(resolve => {
              setTimeout(resolve, 400);
            }));

            this.$refs.gameViewInstance.displayVoteView = true;
            this.$refs.gameViewInstance.turnInfo = this.gameState.turnInfo;
            this.$refs.gameFooterInstance.gameState = this.gameState;
            this.displayTimer = true;
          }
        } else {
          this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSong('voting');
          this.displayTimer = false;

          await (new Promise(resolve => {
            setTimeout(resolve, 600);
          }));

          this.$refs.gameViewInstance.displayVoteStartView = true;

          if ('turnInfo' in this.gameState && this.gameState.turnInfo.wereAllAnswersSubmitted) {
            await (new Promise(resolve => {
              setTimeout(resolve, 2000);
            }));
            this.$refs.gameViewInstance.displayVoteStartView = false;
            await (new Promise(resolve => {
              setTimeout(resolve, 400);
            }));

            this.$refs.gameViewInstance.displayVoteView = true;
            this.$refs.gameViewInstance.turnInfo = this.gameState.turnInfo;
            this.$refs.gameFooterInstance.gameState = this.gameState;
            this.displayTimer = true;
          }
        }
      }
    },
    disableLocalAudioTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.disableAudioTracks();
      this.isLocalAudioMuted = true;
    },
    enableLocalAudioTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.enableAudioTracks();
      this.isLocalAudioMuted = false;
    },
    disableLocalVideoTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.disableVideoTracks();
      this.isLocalVideoMuted = true;
    },
    enableLocalVideoTracks () {
      this.$refs.videoViewInstance.$refs.localVideoComponentInstance.enableVideoTracks();
      this.isLocalVideoMuted = false;
    },
    createNewGame() {
      if (this.isHost) {
        this.playAgainButtonClicked = true;
        this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/createfollowupgame`, {
          auth0UserId: this.auth0UserId,
          username: this.username,
          previousGameRoomcode: this.gameRoomcode
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        }).then(() => {
          this.displayPlayAgainButton = false;
          // wait a bit before resetting playAgainButtonClicked so the user doesn't see it
          setTimeout(() => {
            this.playAgainButtonClicked = false;
          }, 3000);
        }).catch(error => {
          console.log(error);
          this.playAgainButtonClicked = false;
        });
      }
    },
    updateTimeRemaining () {
      if (this.displayTimer && this.isTimestampAvailable && this.gameState.roundIndex < this.gameState.numRounds) {
        this.totalSecondsRemaining = (new Date(this.gameState.countdownTimestamp).getTime() - Date.now()) / 1000;
        const timeLimit = this.gameState.turnIndex === -1 ? 90 : 30;

        if (this.totalSecondsRemaining < timeLimit + 1) {
          let minutesRemaining = '0';
          let secondsRemaining = '00';

          if (this.totalSecondsRemaining > 0) {
            minutesRemaining = String(Math.floor(this.totalSecondsRemaining / 60));
            secondsRemaining = String(Math.floor(this.totalSecondsRemaining % 60));

            if (secondsRemaining < 10) {
              secondsRemaining = '0' + secondsRemaining;
            }
          }

          this.timeRemaining = minutesRemaining + ':' + secondsRemaining;
        } else {
          this.timeRemaining = '';
        }

        // Timer sound effects
        const flooredTotalSecondsRemaining = Math.floor(this.totalSecondsRemaining);

        if (timeLimit === 90) {
          if (flooredTotalSecondsRemaining === 30) {
            this.playSoundEffect('timer-30');
          }
        }

        if (flooredTotalSecondsRemaining === 10) {
          this.playSoundEffect('timer-10');
        } else if (flooredTotalSecondsRemaining === 9) {
          this.playSoundEffect('timer-9-to-1');
        } else if (flooredTotalSecondsRemaining === 0) {
          this.playSoundEffect('timer-0');
        }
      }
    },
    playSoundEffect(soundEffectName) {
      this.$refs.gameFooterInstance.$refs.musicPlayerInstance.playSoundEffect(soundEffectName);
    },
    stopAllSoundEffects() {
      this.$refs.gameFooterInstance.$refs.musicPlayerInstance.stopAllSoundEffects();
    }
  },
  created: function () {
    if (!this.wasRedirectedFromGameSetupScreen) {
      this.$router.push({
        name: 'home',
      });
    } else {
      this.canLoadGameScreen = true;
      this.displayWeavrSpinner = true;
      this.setGameRoomcode(this.initialGameRoomcode);
    }
  },
  beforeMount: function () {
    if (this.canLoadGameScreen) {
      window.addEventListener('beforeunload', this.preventPageUnload);
      window.addEventListener('unload', this.exitGame);
    }
  },
  mounted: function() {
    this.$nextTick(function() {
      if (this.canLoadGameScreen) {
        this.connectSocket();
        this.getGameState();
        this.timerSetIntervalId = setInterval(this.updateTimeRemaining, 1000);
      }
    });
  },
  beforeDestroy: function () {
    if (this.canLoadGameScreen) {
      this.exitGame();
      window.removeEventListener('beforeunload', this.preventPageUnload);
      window.removeEventListener('unload', this.exitGame);
      this.localTracks.forEach(track => {
        track.stop();
      });

      if (this.pingSetIntervalId !== null) {
        clearInterval(this.pingSetIntervalId);
      }

      if (this.timerSetIntervalId !== null) {
        clearInterval(this.timerSetIntervalId);
      }
    }
  }
})
</script>

<!-- No "scoped" attribute so that we can apply overscroll property to body -->
<style lang="scss">
body {
  overscroll-behavior: none;
}

.backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  min-height: 30em;
  background-color: $natural-white;
}

.weavr-spinner-container {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: $eigengrau;
}

.weavr-spinner {
  position: absolute;
  height: 16em;
  width: 16em;
  left: 50%;
  top: 50%;
  margin-left: -8em;
  margin-top: -8em;
  animation: change-colors 6s ease-in-out infinite;
}

.play-again-button {
  display: flex;
  position: absolute;
  bottom: 4em;
  left: 50%;
  font-size: 2em;
  justify-content: center;
  align-items: center;
  transform: translateX(-50%);
  width: 8em;
  height: 2em;
  text-align: center;
  margin: 0 auto;
  border-radius: 0.4em;
  border: 0.07em solid $brand-teal;
  background-color: white;
  cursor: pointer;
  box-shadow: 0 6px 8px -4px $smoke-gray;
}

.play-again-button:hover {
  filter: brightness(0.95);
}

.play-again-button-content {
  flex: 1;
}

.follow-up-game-message-container {
  position: absolute;
  top: 1.4em;
  left: 50%;
  transform: translateX(-50%);
}

.follow-up-game-message {
  font-size: 1.2em;
  border-radius: 0.5em;
  background-color: $baby-blue;
  padding: 0.4em 0.8em;
}

.fade-enter-active {
  transition: opacity 300ms ease-in-out;
}

.fade-leave-active {
  transition: opacity 300ms ease-in-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-slow-enter-active {
  transition: opacity 1000ms ease-out;
}

.fade-slow-leave-active {
  transition: opacity 1000ms ease-out;
}

.fade-slow-enter, .fade-slow-leave-to {
  opacity: 0;
}

.fade-delay-enter-active {
  transition: opacity 300ms ease-in-out 300ms;
}

.fade-delay-leave-active {
  transition: opacity 300ms ease-in-out;
}

.fade-delay-enter, .fade-delay-leave-to {
  opacity: 0;
}

.pop-from-top-enter-active {
  transform-origin: top center;
  animation: pop 300ms ease-out;
}

.pop-from-top-leave-active {
  transform-origin: top center;
  animation: pop 300ms ease-out reverse;
}

.pop-from-top-big-enter-active {
  transform-origin: top center;
  animation: pop-big 400ms ease-out;
}

.pop-from-top-big-leave-active {
  transform-origin: top center;
  animation: pop-big 400ms ease-out reverse;
}

.pop-from-top-delay-enter-active {
  transform-origin: top center;
  animation: pop-delay 900ms ease-out;
}

.pop-from-top-delay-leave-active {
  transform-origin: top center;
  animation: pop 300ms ease-out reverse;
}

.pop-from-bottom-enter-active {
  transform-origin: bottom center;
  animation: pop 300ms ease-out;
}

.pop-from-bottom-leave-active {
  transform-origin: bottom center;
  animation: pop 300ms ease-out reverse;
}

.fade-up-enter-active {
  transform-origin: bottom center;
  animation: fade-in-and-up 700ms ease-out;
}

.fade-up-leave-active {
  transform-origin: bottom center;
  animation: fade-out-and-up 700ms ease-out;
}

@keyframes change-colors {
  from {
    filter: hue-rotate(0);
  }
  50% {
    filter: hue-rotate(180deg);
  }
  to {
    filter: hue-rotate(0);
  }
}

@keyframes pop {
  from {
    transform: scale(0.9, 0.9);
    opacity: 0;
  }

  50% {
    transform: scale(1.02, 1.02);
    opacity: 1;
  }

  to {
    transform: scale(1, 1);
    opacity: 1;
  }
}

@keyframes pop-delay {
  from {
    opacity: 0;
  }

  50% {
    transform: scale(0.9, 0.9);
    opacity: 0;
  }

  75% {
    transform: scale(1.02, 1.02);
    opacity: 1;
  }

  to {
    transform: scale(1, 1);
    opacity: 1;
  }
}

@keyframes pop-big {
  from {
    transform: scale(0.6, 0.6);
    opacity: 0;
  }

  50% {
    transform: scale(1.1, 1.1);
    opacity: 1;
  }

  to {
    transform: scale(1, 1);
    opacity: 1;
  }
}

@keyframes change-brightness {
  from {
    filter: brightness(100%);
  }
  50% {
    filter: brightness(115%);
  }
  to {
    filter: brightness(100%);
  }
}

@keyframes change-brightness-extreme {
  from {
    filter: brightness(75%);
  }
  50% {
    filter: brightness(150%);
  }
  to {
    filter: brightness(75%);
  }
}

@keyframes fade-in-and-up {
  from {
    transform: translateY(3em);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fade-out-and-up {
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(-3em);
    opacity: 0;
  }
}
</style>
